import * as THREE from 'three';
import store from '../../redux/store';
import {
  selected_object,
  setPushIntersectedObject,
} from '../../redux/Slice/gridSlice';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import {
  startAnimation,
  stopAnimation,
  pauseAnimation,
} from './animationControls';
import { setLifeTimeout, clearLifeTimeout } from './lifeUtils';
import {
  createParticleTexture,
  applyParticleShapeMap,
  getDefaultParticleShape,
} from './particleShapeTextures';
import { hasValidParticleCount } from '../ThreeJsUtils/assetVisibilityUtils';
import { ensurePointsMaterialOrthoSupport, resyncSceneParticlePoints } from './syncParticlePointSizesForCamera';

// Resolve blending value (string or numeric) to THREE blending constant
const resolveBlending = (value) => {
  if (typeof value === 'number') return value;
  if (!value) return THREE.NormalBlending;
  const v = String(value).toLowerCase();
  if (v.includes('add')) return THREE.AdditiveBlending;
  if (v.includes('sub')) return THREE.SubtractiveBlending;
  if (v.includes('mul')) return THREE.MultiplyBlending;
  return THREE.NormalBlending;
};

// ✅ Helper: clamp a particle back inside ±halfRange on X and Z.
// Instead of hard-clamping (which snaps visually), we wrap the particle
// to the opposite edge so it re-enters naturally — same feel as the Y reset.
const wrapParticleInBounds = (positions, i, halfRange) => {
  // X axis wrap
  if (positions[i] > halfRange) {
    positions[i] = -halfRange + (positions[i] - halfRange);
  } else if (positions[i] < -halfRange) {
    positions[i] = halfRange + (positions[i] + halfRange);
  }

  // Z axis wrap
  if (positions[i + 2] > halfRange) {
    positions[i + 2] = -halfRange + (positions[i + 2] - halfRange);
  } else if (positions[i + 2] < -halfRange) {
    positions[i + 2] = halfRange + (positions[i + 2] + halfRange);
  }
};

const handleSnow = (scene, particle) => {
  if (!scene) return;

  const particleCount = particle?.particleCount !== undefined ? particle.particleCount : 1000;
  const range = particle?.range !== undefined ? particle.range : 50;
  const height = particle?.height !== undefined ? particle.height : 30;
  const color = particle?.color !== undefined ? particle.color : '#ffffff';
  const opacity = particle?.opacity !== undefined ? particle.opacity : 0.9;
  const size = particle?.size !== undefined ? particle.size : 0.4;
  const windStrength = particle?.windStrength !== undefined ? particle.windStrength : 0.5;
  const swirlStrength = particle?.swirlStrength !== undefined ? particle.swirlStrength : 0.03;
  const accumulationSpeed = particle?.accumulationSpeed !== undefined ? particle.accumulationSpeed : 0.001;
  const shape = particle?.shape ?? getDefaultParticleShape('snow');
  const shapeRotation = particle?.shapeRotation !== undefined ? particle.shapeRotation : 0;
  const shapeScale = particle?.shapeScale !== undefined ? particle.shapeScale : 1.0;
  const life = particle?.life !== undefined ? particle.life : 3.0;
  const lifeVariation = particle?.lifeVariation !== undefined ? particle.lifeVariation : 0.3;

  const snowGroup = new THREE.Group();

  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(particleCount * 3);
  const velocities = new Float32Array(particleCount * 3);
  const sizes = new Float32Array(particleCount);
  const rotations = new Float32Array(particleCount);
  const lifeArray = new Float32Array(particleCount);
  const maxLifeArray = new Float32Array(particleCount);

  for (let i = 0; i < particleCount; i++) {
    positions[i * 3] = (Math.random() - 0.5) * range;
    positions[i * 3 + 1] = Math.random() * height;
    positions[i * 3 + 2] = (Math.random() - 0.5) * range;

    velocities[i * 3] = Math.sin(i * 0.1) * 0.2;
    velocities[i * 3 + 1] = -(Math.random() * 0.3 + 0.1);
    velocities[i * 3 + 2] = Math.cos(i * 0.1) * 0.2;

    sizes[i] = Math.random() * 0.8 + 0.2;
    rotations[i] = Math.random() * Math.PI * 2;

    const particleLife = life * (1 + (Math.random() - 0.5) * lifeVariation);
    maxLifeArray[i] = particleLife;
    lifeArray[i] = particleLife;
  }

  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

  const snowColor = particle?.color
    ? new THREE.Color(particle.color)
    : new THREE.Color(color);

  const material = new THREE.PointsMaterial({
    color: snowColor,
    size: size * shapeScale,
    transparent: true,
    opacity: opacity,
    sizeAttenuation: true,
    depthWrite: false,
    alphaTest: 0.01,
    map: createParticleTexture(shape, color, 64, shapeRotation, shapeScale),
  });
  material.userData = material.userData || {};
  material.userData.authoredSize = size * shapeScale;
  ensurePointsMaterialOrthoSupport(material);

  const snow = new THREE.Points(geometry, material);
  snowGroup.add(snow);

  snowGroup.name = 'snow';
  snowGroup.deleteType = 'particle';
  snowGroup.objType = 'snow';
  const uniqueId = `snow_${Date.now()}`;

  if (particle && particle.id) {
    snowGroup.name = particle.id;
  } else if (particle && particle._id) {
    snowGroup.name = particle._id;
  } else {
    snowGroup.name = uniqueId;
  }

  snowGroup.displayName = 'Snow';
  snowGroup.url = uniqueId;
  snowGroup.asset_file_name = 'snow';
  snowGroup.file_name = 'snow';
  snowGroup.mesh_code = 'snow';
  snowGroup.glb_type = 'shapes';
  snowGroup.createdAt = new Date().toISOString();
  snowGroup.userData = {
    material_settings: {
      shader: 'Standard',
      albedo: color,
      roughness: 0.9,
      metalness: 0.0,
    },
    particleCount,
      range,
    height,
    color,
    opacity,
    size,
    windStrength,
    swirlStrength,
    accumulationSpeed,
    shape,
    shapeRotation,
    shapeScale,
  };

  if (particle?.position) {
    snowGroup.position.set(
      particle.position.x || 0,
      particle.position.y || 0,
      particle.position.z || 0
    );
  }
  if (particle?.rotation) {
    snowGroup.rotation.set(
      particle.rotation.x || 0,
      particle.rotation.y || 0,
      particle.rotation.z || 0
    );
  }
  if (particle?.scale) {
    snowGroup.scale.set(
      particle.scale.x || 1,
      particle.scale.y || 1,
      particle.scale.z || 1
    );
  }

  scene.add(snowGroup);
  resyncSceneParticlePoints(scene);
  store.dispatch(setPushIntersectedObject(snowGroup));

  const animate = () => {
    if (!snowGroup.parent) return;
    const time = Date.now() * 0.001;
    const positions = geometry.attributes.position.array;

    const currentConfig = snowGroup.userData;
    const currentRange = currentConfig?.range !== undefined ? currentConfig.range : range;
    const currentHeight = currentConfig?.height !== undefined ? currentConfig.height : height;
    const currentWindStrength = currentConfig?.windStrength !== undefined ? currentConfig.windStrength : windStrength;
    const currentSwirlStrength = currentConfig?.swirlStrength !== undefined ? currentConfig.swirlStrength : swirlStrength;

    // ✅ Half-range defines the boundary box on X and Z
    const halfRange = currentRange / 2;

    const windX = Math.sin(time * 0.3) * currentWindStrength;
    const windZ = Math.cos(time * 0.2) * (currentWindStrength * 0.6);

    for (let i = 0; i < positions.length; i += 3) {
      // Move particle with reduced speed (0.4 multiplier)
      positions[i]     += (velocities[i]     + windX * 0.02) * 0.3;
      positions[i + 1] += (velocities[i + 1]) * 0.3;
      positions[i + 2] += (velocities[i + 2] + windZ * 0.02) * 0.3;

      // Swirl motion
      const swirl = Math.sin(time + i * 0.01) * currentSwirlStrength;
      positions[i]     += swirl;
      positions[i + 2] += Math.cos(time + i * 0.01) * currentSwirlStrength;

      // ✅ Wrap X and Z within range boundary
      wrapParticleInBounds(positions, i, halfRange);

      // Reset Y when hitting ground
      if (positions[i + 1] < -4) {
        positions[i]     = (Math.random() - 0.5) * currentRange;
        positions[i + 1] = currentHeight;
        positions[i + 2] = (Math.random() - 0.5) * currentRange;
      }
    }

    geometry.attributes.position.needsUpdate = true;
    snowGroup.userData.animationId = requestAnimationFrame(animate);
  };

  snowGroup.userData.animateFunction = animate;

  if (!particle?.isStopped) {
    startAnimation(snowGroup, animate, snowGroup.name);
    setLifeTimeout(
      snowGroup,
      typeof life === 'number' ? life : parseFloat(life) || 0
    );
  }

  store.dispatch(selected_object(snowGroup));
  selectObjectInScene(snowGroup.name);
};

const updateSnow = (scene, snowSystem, newParticleData) => {
  if (!scene || !snowSystem || snowSystem.objType !== 'snow') {
    console.warn('Invalid snow system or scene provided for update');
    return;
  }

  const currentConfig = snowSystem.userData || {};

  const updatedConfig = {
    particleCount: newParticleData?.particleCount !== undefined ? newParticleData.particleCount : (currentConfig.particleCount !== undefined ? currentConfig.particleCount : 1000),
    range:        newParticleData?.range !== undefined ? newParticleData.range : (currentConfig.range !== undefined ? currentConfig.range : 50),
    height:        newParticleData?.height !== undefined ? newParticleData.height : (currentConfig.height !== undefined ? currentConfig.height : 50),
    color:         newParticleData?.color !== undefined ? newParticleData.color : (currentConfig.color !== undefined ? currentConfig.color : '#ffffff'),
    opacity:       newParticleData?.opacity !== undefined ? newParticleData.opacity : (currentConfig.opacity !== undefined ? currentConfig.opacity : 0.9),
    size:          newParticleData?.size !== undefined ? newParticleData.size : (currentConfig.size !== undefined ? currentConfig.size : 0.4),
    windStrength:  newParticleData?.windStrength !== undefined ? newParticleData.windStrength : (currentConfig.windStrength !== undefined ? currentConfig.windStrength : 0.5),
    swirlStrength: newParticleData?.swirlStrength !== undefined ? newParticleData.swirlStrength : (currentConfig.swirlStrength !== undefined ? currentConfig.swirlStrength : 0.03),
    accumulationSpeed: newParticleData?.accumulationSpeed !== undefined ? newParticleData.accumulationSpeed : (currentConfig.accumulationSpeed !== undefined ? currentConfig.accumulationSpeed : 0.001),
    shape:
      newParticleData?.shape ??
      currentConfig.shape ??
      getDefaultParticleShape('snow'),
    shapeRotation: newParticleData?.shapeRotation !== undefined ? newParticleData.shapeRotation : (currentConfig.shapeRotation !== undefined ? currentConfig.shapeRotation : 0),
    shapeScale:    newParticleData?.shapeScale !== undefined ? newParticleData.shapeScale : (currentConfig.shapeScale !== undefined ? currentConfig.shapeScale : 1.0),
    blending:      newParticleData?.blending !== undefined ? newParticleData.blending : (currentConfig.blending !== undefined ? currentConfig.blending : 'normal'),
    life:          newParticleData?.life !== undefined ? newParticleData.life : (currentConfig.life !== undefined ? currentConfig.life : 3.0),
    lifeVariation: newParticleData?.lifeVariation !== undefined ? newParticleData.lifeVariation : (currentConfig.lifeVariation !== undefined ? currentConfig.lifeVariation : 0.3),
  };

  // Handle animation state changes
  if (
    typeof newParticleData?.isPlaying !== 'undefined' ||
    typeof newParticleData?.isStopped !== 'undefined'
  ) {
    const isPlaying = newParticleData?.isPlaying ?? currentConfig.isPlaying ?? true;
    const isStopped = newParticleData?.isStopped ?? currentConfig.isStopped ?? false;

    if (isStopped) {
      stopAnimation(snowSystem, snowSystem.name);
    } else if (isPlaying) {
      const animateFunction = snowSystem.userData.animateFunction;
      if (animateFunction) {
        startAnimation(snowSystem, animateFunction, snowSystem.name);
      }
    } else {
      pauseAnimation(snowSystem, snowSystem.name);
    }
  }

  const snowPoints = snowSystem.children.find(
    (child) => child instanceof THREE.Points
  );

  // Rebuild geometry if particle count changed
  if (
    snowPoints &&
    hasValidParticleCount(updatedConfig.particleCount) &&
    updatedConfig.particleCount !== currentConfig.particleCount
  ) {
    snowPoints.geometry.dispose();

    const newGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(updatedConfig.particleCount * 3);
    const velocities = new Float32Array(updatedConfig.particleCount * 3);
    const sizes = new Float32Array(updatedConfig.particleCount);
    const rotations = new Float32Array(updatedConfig.particleCount);

    for (let i = 0; i < updatedConfig.particleCount; i++) {
      positions[i * 3]     = (Math.random() - 0.5) * updatedConfig.range;
      positions[i * 3 + 1] = Math.random() * updatedConfig.height;
      positions[i * 3 + 2] = (Math.random() - 0.5) * updatedConfig.range;

      velocities[i * 3]     = Math.sin(i * 0.1) * 0.2;
      velocities[i * 3 + 1] = -(Math.random() * 0.3 + 0.1);
      velocities[i * 3 + 2] = Math.cos(i * 0.1) * 0.2;

      sizes[i]     = Math.random() * 0.8 + 0.2;
      rotations[i] = Math.random() * Math.PI * 2;
    }

    newGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    newGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    snowPoints.geometry = newGeometry;
    snowPoints.userData.velocities = velocities;
    snowPoints.userData.rotations = rotations;
  }

  const rangeChanged = newParticleData?.range !== undefined && newParticleData.range !== currentConfig.range;
  const heightChanged = newParticleData?.height !== undefined && newParticleData.height !== currentConfig.height;

  // Scale positions if range/height changed (same particle count)
  if (
    snowPoints &&
    snowPoints.geometry &&
    (rangeChanged || heightChanged) &&
    updatedConfig.particleCount === currentConfig.particleCount
  ) {
    const positions = snowPoints.geometry.attributes.position.array;
    
    // Scale existing positions to smoothly expand/contract without jarring resets
    const rangeRatio = rangeChanged ? (updatedConfig.range / (currentConfig.range || 1)) : 1;
    const heightRatio = heightChanged ? (updatedConfig.height / (currentConfig.height || 1)) : 1;

    for (let i = 0; i < positions.length; i += 3) {
      if (rangeChanged) {
        positions[i]     *= rangeRatio;
        positions[i + 2] *= rangeRatio;
      }
      if (heightChanged) {
        positions[i + 1] *= heightRatio;
      }
    }
    snowPoints.geometry.attributes.position.needsUpdate = true;
    snowPoints.geometry.computeBoundingBox();
    snowPoints.geometry.computeBoundingSphere();
    
    // Force TransformControls to update its bounding box gizmo immediately
    if (snowSystem.userData) snowSystem.userData.forceBoxUpdate = true;
  }

  // Update material
  if (snowPoints && snowPoints.material) {
    snowPoints.material.color.copy(new THREE.Color(updatedConfig.color));
    if (newParticleData?.opacity !== undefined) {
      snowPoints.material.transparent = updatedConfig.opacity < 1;
      snowPoints.material.opacity = updatedConfig.opacity;
    }
    if (newParticleData?.size !== undefined) {
      snowPoints.material.size = updatedConfig.size * updatedConfig.shapeScale;
    }
    if (newParticleData?.blending !== undefined) {
      snowPoints.material.blending = resolveBlending(updatedConfig.blending);
      snowPoints.material.depthWrite = false;
    }
    if (
      newParticleData?.shape !== undefined ||
      newParticleData?.color !== undefined ||
      newParticleData?.shapeRotation !== undefined ||
      newParticleData?.shapeScale !== undefined
    ) {
      applyParticleShapeMap(
        snowPoints.material,
        updatedConfig.shape,
        updatedConfig.color,
        updatedConfig.shapeRotation,
        updatedConfig.shapeScale
      );
    }
    snowPoints.material.needsUpdate = true;
  }

  // Update snow cover material color
  const snowCover = snowSystem.children.find(
    (child) => child.material && child.material.opacity === 0.3
  );
  if (snowCover && snowCover.material) {
    snowCover.material.color.copy(new THREE.Color(updatedConfig.color));
  }

  // Update transforms
  if (newParticleData?.position) {
    snowSystem.position.set(
      newParticleData.position.x !== undefined ? newParticleData.position.x : snowSystem.position.x,
      newParticleData.position.y !== undefined ? newParticleData.position.y : snowSystem.position.y,
      newParticleData.position.z !== undefined ? newParticleData.position.z : snowSystem.position.z
    );
  }
  if (newParticleData?.rotation) {
    snowSystem.rotation.set(
      newParticleData.rotation.x !== undefined ? newParticleData.rotation.x : snowSystem.rotation.x,
      newParticleData.rotation.y !== undefined ? newParticleData.rotation.y : snowSystem.rotation.y,
      newParticleData.rotation.z !== undefined ? newParticleData.rotation.z : snowSystem.rotation.z
    );
  }
  if (newParticleData?.scale) {
    snowSystem.scale.set(
      newParticleData.scale.x !== undefined ? newParticleData.scale.x : snowSystem.scale.x,
      newParticleData.scale.y !== undefined ? newParticleData.scale.y : snowSystem.scale.y,
      newParticleData.scale.z !== undefined ? newParticleData.scale.z : snowSystem.scale.z
    );
  }

  // Persist updated config to userData
  snowSystem.userData = {
    ...snowSystem.userData,
    ...updatedConfig,
    material_settings: {
      ...snowSystem.userData.material_settings,
      albedo: updatedConfig.color,
    },
  };

  // Rename system if ID changed
  if (newParticleData?.id && newParticleData.id !== snowSystem.name) {
    snowSystem.name = newParticleData.id;
    snowSystem.url  = newParticleData.id;
  }

  // Restart animation with boundary clamping if particle count changed
  if (
    hasValidParticleCount(updatedConfig.particleCount) &&
    updatedConfig.particleCount !== currentConfig.particleCount &&
    !newParticleData?.isStopped &&
    newParticleData?.isPlaying !== false
  ) {
    if (snowSystem.userData.animationId) {
      cancelAnimationFrame(snowSystem.userData.animationId);
    }

    const animate = () => {
      if (!snowSystem.parent) {
        snowSystem.userData.animationId = null;
        return;
      }

      const time = Date.now() * 0.001;
      const snowPoints = snowSystem.children.find(
        (child) => child instanceof THREE.Points
      );
      if (!snowPoints || !snowPoints.geometry) return;

      const positions  = snowPoints.geometry.attributes.position.array;
      const velocities = snowPoints.userData.velocities || [];

      // Read current config dynamically from userData so updates apply immediately
      const activeConfig = snowSystem.userData;
      const currentRange = activeConfig?.range !== undefined ? activeConfig.range : updatedConfig.range;
      const currentHeight = activeConfig?.height !== undefined ? activeConfig.height : updatedConfig.height;
      const currentWindStrength = activeConfig?.windStrength !== undefined ? activeConfig.windStrength : updatedConfig.windStrength;
      const currentSwirlStrength = activeConfig?.swirlStrength !== undefined ? activeConfig.swirlStrength : updatedConfig.swirlStrength;
      const currentAccumulationSpeed = activeConfig?.accumulationSpeed !== undefined ? activeConfig.accumulationSpeed : updatedConfig.accumulationSpeed;

      // ✅ Boundary half-extents
      const halfRange = currentRange / 2;

      const windX = Math.sin(time * 0.3) * currentWindStrength;
      const windZ = Math.cos(time * 0.2) * (currentWindStrength * 0.6);

      for (let i = 0; i < positions.length; i += 3) {
        // Move particle with reduced speed (0.4 multiplier)
        positions[i]     += (velocities[i]     + windX * 0.02) * 0.4;
        positions[i + 1] += (velocities[i + 1]) * 0.4;
        positions[i + 2] += (velocities[i + 2] + windZ * 0.02) * 0.4;

        const swirl = Math.sin(time + i * 0.01) * currentSwirlStrength;
        positions[i]     += swirl;
        positions[i + 2] += Math.cos(time + i * 0.01) * currentSwirlStrength;

        // ✅ Wrap X and Z within range boundary
        wrapParticleInBounds(positions, i, halfRange);

        // Reset Y when hitting ground
        if (positions[i + 1] < -4) {
          positions[i]     = (Math.random() - 0.5) * currentRange;
          positions[i + 1] = currentHeight;
          positions[i + 2] = (Math.random() - 0.5) * currentRange;
        }
      }

      // Animate snow cover accumulation
      const snowCover = snowSystem.children.find(
        (child) => child.material && child.material.opacity === 0.3
      );
      if (snowCover && snowCover.material) {
        snowCover.material.opacity = Math.min(
          0.6,
          snowCover.material.opacity + currentAccumulationSpeed
        );
      }

      snowPoints.geometry.attributes.position.needsUpdate = true;
      snowSystem.userData.animationId = requestAnimationFrame(animate);
    };

    snowSystem.userData.animateFunction = animate;
    snowSystem.userData.animationId = requestAnimationFrame(animate);
  }

  store.dispatch(setPushIntersectedObject(snowSystem));

  // Manage life timeout
  try {
    clearLifeTimeout(snowSystem);
    const lifeVal = updatedConfig?.life;
    const lifeSeconds = typeof lifeVal === 'number' ? lifeVal : parseFloat(lifeVal);
    if (!isNaN(lifeSeconds) && lifeSeconds > 0) {
      setLifeTimeout(snowSystem, lifeSeconds);
      snowSystem.visible = true;
      if (snowSystem.userData) {
        snowSystem.userData.isStopped = false;
        snowSystem.userData.isPlaying = true;
      }
    }
  } catch (e) {
    // ignore
  }

  resyncSceneParticlePoints(scene);
  return snowSystem;
};

// Helper function to find snow system by name/id in scene
const findSnowSystem = (scene, nameOrId) => {
  return scene.children.find(
    (child) =>
      (child.name === nameOrId || child.url === nameOrId) &&
      child.objType === 'snow'
  );
};

// Convenience function to update snow by name
const updateSnowByName = (scene, nameOrId, newParticleData) => {
  const snowSystem = findSnowSystem(scene, nameOrId);
  if (snowSystem) {
    return updateSnow(scene, snowSystem, newParticleData);
  } else {
    console.warn(`Snow system with name/id "${nameOrId}" not found in scene`);
    return null;
  }
};

export default handleSnow;
export { updateSnow, updateSnowByName };